// v2AuthService.js
import httpClient from '../../../../httpClient';
import { config } from '../../../../config';

const PROD_BACK = config.backend;

/**
 * Sends login credentials to the server.
 * @param {string} email - User email address
 * @param {string} password - User password (plain text — V1 backend limitation)
 * @returns {Promise<Object>} - Raw axios response data
 */
export async function loginUser(email, password) {
  console.log('[v2AuthService.js] r1: loginUser called for email domain=', email?.split('@')[1]);
  const response = await httpClient.post('/ids_checklogin?ui=2', {
    useremail: email,
    password,
  });
  return response.data;
}

/**
 * The browser's IANA time zone (e.g. "Asia/Kolkata"), stored at signup as the user/org display zone.
 * @returns {string} - IANA name, or '' when the browser cannot report one
 */
export function getBrowserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_) {
    return '';
  }
}

/**
 * Registers a new user and creates an organisation.
 * @param {Object} payload
 * @param {string} payload.email      - Work email
 * @param {string} payload.orgName    - Organisation name
 * @param {string} payload.password   - Password
 * @param {string} payload.userName   - Full name
 * @param {string} payload.dialCode   - Dial code digits (no '+'), e.g. "1"
 * @param {string} payload.phone      - Phone digits only, no country code
 * @param {string} payload.country    - Country display name, e.g. "United States"
 * @returns {Promise<Object>} - Raw axios response data
 */
export async function registerUser({ email, orgName, password, userName, dialCode, phone, country }) {
  const response = await httpClient.post('/ids_register', {
    email,
    org_name: orgName,
    password,
    user_name: userName,
    code: dialCode,
    phone,
    country,
    time_zone: getBrowserTimeZone(),
  });
  return response.data;
}

/**
 * Exchanges a one-time Okta code for a JWT session.
 * @param {string} code - One-time code from URL hash after Okta callback
 * @returns {Promise<Object>} - Auth payload (same shape as loginUser success)
 */
export async function exchangeOktaCode(code) {
  const response = await httpClient.get(`/auth/okta/exchange?code=${encodeURIComponent(code)}`);
  return response.data;
}

/**
 * Builds the Okta SSO redirect URL.
 * @param {'login'|'signup'} purpose - Whether user is logging in or signing up
 * @param {string} email             - Work email collected from modal
 * @returns {string} - Full URL for window.location.href redirect
 */
export function buildOktaRedirectUrl(purpose, email) {
  const path = purpose === 'signup' ? '/auth/okta/signup' : '/auth/okta/login';
  return `${PROD_BACK}${path}?email=${encodeURIComponent(email)}`;
}

/**
 * Opens the forgot-password server-rendered HTML page.
 * @returns {void}
 */
export function openForgotPasswordPage() {
  window.open(`${PROD_BACK}/mail_confirm?ui=1`, '_blank');
  try { localStorage.removeItem('created_timestamp'); } catch (_) {}
}

/**
 * Opens the unverified-user re-verification page.
 * @param {string} email - The unverified user's email
 * @returns {void}
 */
export function openVerificationPage(email) {
  const url = `${PROD_BACK}/user_verification_mail?ui=2&useremail=${encodeURIComponent(email)}`;
  window.open(url, '_blank');
}

/**
 * Persists auth payload to localStorage and fires the userJourneyRefresh event.
 * @param {Object} data - Success response from loginUser or exchangeOktaCode
 */
export function setLocalStorageAuth(data) {
  localStorage.setItem('ids_token', data.data);
  localStorage.setItem('ids_auth_level', data.ids_auth_level);
  localStorage.setItem('ids_user_id', data.user_id);
  localStorage.setItem('ids_user_name', data.user_name);
  localStorage.setItem('ids_org_id', data.ids_org_id);
  localStorage.setItem('ids_org_name', data.org_name);
  localStorage.setItem('ids_user_mail', data.user_mail);
  localStorage.setItem('ids_is_free_trial', data.free_trial);
  localStorage.setItem('ids_first_time_login', data.ids_first_time_login);
  localStorage.setItem('ids_should_show_guide', data.ids_should_show_guide);
  localStorage.setItem('internal_org_status', data.internal_org_status);
  window.dispatchEvent(new CustomEvent('userJourneyRefresh'));
}

/**
 * Personal email domain blocklist for Okta modal validation.
 * (Same list as V1 isWorkEmail check)
 */
const PERSONAL_DOMAINS = new Set([
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
  'live.com', 'aol.com', 'icloud.com', 'protonmail.com',
]);

/**
 * Returns true if the email is from a business domain (not personal).
 * @param {string} email
 * @returns {boolean}
 */
export function isWorkEmail(email) {
  const domain = (email || '').split('@')[1]?.toLowerCase() ?? '';
  return domain.length > 0 && !PERSONAL_DOMAINS.has(domain);
}
